/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard React components
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import { IconButton, Tooltip } from "@mui/material";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Permissions
import { useAbility } from "@casl/react";
import { AbilityContext, Can } from "Can";
import { AuthContext } from "context";

// E4A React components
import ConfirmDialog from "e4acomponents/ConfirmDialog";
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";

// Data
import partCodesService from "services/part-code-service";
import formatPartCode from "helper/formatPartCode";

function PartView() {

  // Data
  const [parts, setParts] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  // Permissions
  const ability = useAbility(AbilityContext);
  const authContext = useContext(AuthContext);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "Delete (Archive) Part Code?",
    prompt: "Are you sure you want to delete (archive) this part code?",
    onClose: () => { }
  })

  // Fetch part codes
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const response = await partCodesService.getParts();
        setParts(response.data.attributes);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Update table's rows at the slightest change
  useEffect(() => {
    setTableData(getRows(parts));
  }, [parts]);

  // Handler to navigate to the specific view page of the part code
  const goToViewPage = (id) => {
    navigate(`/part-code-management/view-part-code/${id}`);
  }

  // Format data for DataTable
  const getRows = (info) => {
    let updatedInfo = info
      // Decomment this line to filter partcodes by status .filter(e => (e.status === "Approved"))
      .map((e) => {
        return {
          categoryCode: formatPartCode(e.unique_num, e.partCategory.categoryCode, e.revision_num),
          id: e._id,
          manufacturerPartNum: e.mpn ? e.mpn[e.preferred_mpn] : e.manufacturer_part_number,
          PartCategory: e.partCategory.description,
          description: e.has_attributes_filled ? description(e) : e.description,
          statusChip: <Chip label={e.status} color={statusColor(e.status)} variant="outlined" />,
          status: e.status,
          mpn: (() => {
            const mpnValues = Object.values(e.mpn || {}).filter((v) => v && v.trim());
            if (mpnValues.length > 0) {
              return mpnValues.join(" ");
            }
            return e.manufacturer_part_number;
          })(),
        };
      });
    return updatedInfo
  };

  const statusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending Approval":
        return "warning";
      case "Rejected":
        return "error";
      case "Disabled":
        return "secondary";
      default:
        return "default";
    }
  }

  // Helper function to generate the detailed description based on the attributes of the partcode
  const description = (partCode) => {
    const categoryCode = partCode.partCategory.categoryCode;
    return partCode.description.startsWith(categoryCode)
      ? partCode.description
      : `${categoryCode} ${partCode.description}`;
  }

  const handleCloseDialog = async (response, title, id) => {
    if (response) {
      await clickDeleteHandler(id);
    }
    setOpenDialog(false);
  }

  const handleOpenDialog = (id) => {
    setDialogProps({
      ...dialogProps,
      onClose: handleCloseDialog,
      id: id
    })
    setOpenDialog(true);
  }

  // Array containing the dataTable's headers and rows
  const dataTableData = {
    columns: [
      {
        Header: "Part Code",
        accessor: "categoryCode",
        width: "15%",
        Cell: (info) => {
          return (
            <MDTypography
              component="a"
              href="#"
              variant="button"
              fontWeight="medium"
              onClick={() => goToViewPage(info.cell.row.original.id)}
              sx={{ cursor: "pointer", textDecoration: "none", color: "#344767" }}
            >
              {info.cell.value}
            </MDTypography>
          );
        }
      },
      { Header: "Part Category", accessor: "PartCategory", width: "12%" },
      {
        Header: "Manufacturer Part Number",
        accessor: "mpn",
        width: "13%",
        Cell: ({ row }) => {
          return row.original.manufacturerPartNum
        }
      },
      { Header: "Description", accessor: "description", width: "25%" },
      {
        Header: "Status",
        accessor: "status",
        width: "15%",
        Cell: ({ row }) => row.original.statusChip,
        type: 'singleSelect',
        valueOptions: ['Approved', 'Rejected', 'Pending Approval', 'Disabled', 'Obsolete'],
      },
      {
        Header: "Actions",
        disableSortBy: true,
        accessor: "actions",
        width: "15%",
        Cell: (info) => {
          const status = info.cell.row.original.status.toLowerCase();
          const id = info.cell.row.original.id;
          return (

            <MDBox display="flex" alignItems="center">
              {/* Conditional rendering of buttons based on the status of the part code */}

              <>
                {/* Delete button */}
                {
                  (status === "Approved") ? (
                    <Can I="delete" this={"Approved-PartCode"}>
                      <Tooltip
                        title="Delete Part"
                        onClick={() => handleOpenDialog(id)}
                      >
                        <IconButton >
                          <DeleteTwoToneIcon
                            color="error" />
                        </IconButton>
                      </Tooltip>
                    </Can>
                  ) : (
                    <Can I="Delete" this={"PartCode"}>
                      <Tooltip
                        title="Delete Part"
                        onClick={() => handleOpenDialog(id)}
                      >
                        <IconButton disabled={(status === "disabled" || status === "obsolete")}>
                          <DeleteTwoToneIcon
                            color={(status === "disabled" || status === "obsolete") ? "disabled" : "error"}
                          />
                        </IconButton>
                      </Tooltip>
                    </Can>
                  )}

                {/* Edit button */}
                <Can I="access" this={"edit-part-code"}>
                  <Tooltip
                    title="Edit Part"
                    onClick={() => clickEditHandler(id)}
                  >
                    <IconButton disabled={(status === "disabled" || status === "obsolete")}>
                      <EditTwoToneIcon
                        color={(status === "disabled" || status === "obsolete") ? "disabled" : "success"} />
                    </IconButton>
                  </Tooltip>
                </Can>
              </>

              {/*View Part Code Button */}
              < Tooltip
                title="View Part"
                onClick={() => navigate(`/part-code-management/view-part-code/${id}`)}
              >
                <IconButton>
                  <VisibilityTwoToneIcon color="info" />
                </IconButton>
              </Tooltip>

            </MDBox >

          );
        },
      },
    ],
    rows: tableData
  };

  // Behaviour when clicking the create part button
  const clickCreateHandler = () => {
    navigate("/part-code-management/create-part-code");
  };
  // Behaviour when clicking the edit part button
  const clickEditHandler = (id) => {
    navigate(`/part-code-management/edit-part-code/${id}`);
  };
  // Behaviour when clicking the delete part button
  const clickDeleteHandler = async (id) => {
    try {
      const payload = {
        data: {
          attributes: {
            status: "Archived"
          },
          logs: {
            user: await authContext.getCurrentUser(),
            comment: ""
          }
        }
      }
      await partCodesService.editStatus(id, payload);
      // Reload the dataTable
      const response = await partCodesService.getParts();
      setParts(response.data.attributes);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <DashboardLayout>

      <ConfirmDialog
        open={openDialog}
        props={dialogProps}
      />

      <DashboardNavbar />

      <MDBox pt={6} pb={3}>

        {/* Main Card */}
        <Card>

          {/* Title and Create Button */}
          <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">
            <MDTypography variant="h5" fontWeight="medium">
              Parts Codes List
            </MDTypography>
            {ability.can("access", "create-part-code") && (
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                type="submit"
                onClick={clickCreateHandler}
              >
                + Create Part Code
              </MDButton>
            )}
          </MDBox>

          {/* Data Table */}
          <MDBox>
            <DataTable
              table={dataTableData}
              canSearch
              isLoading={isLoading}
              tableId="part-codes-table"
            />
          </MDBox>

        </Card>

      </MDBox>

      {/* Footer */}
      <MDBox>
        <Footer />
      </MDBox>

    </DashboardLayout>

  );
}

export default PartView;